import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_CONFIG } from '../config';
import { notifySessionExpired } from './sessionExpiry';

// Bump trigger: 21

const developerApi = axios.create({
  baseURL: API_CONFIG.developerApiBase,
});

developerApi.interceptors.request.use(async (config) => {
  const { tokens } = await fetchAuthSession();
  const token = tokens?.idToken?.toString();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Response error handler: an expired Cognito session makes the developer API
 * return 401. Redirect to a clean re-login instead of surfacing raw 401 text.
 * Exported so the 401→redirect wiring is directly unit-testable. Always
 * rethrows so callers still see the rejection (e.g. ApiErrorAlert).
 */
export function handleDeveloperApiError(error) {
  if (error?.response?.status === 401) notifySessionExpired();
  return Promise.reject(error);
}

developerApi.interceptors.response.use((response) => response, handleDeveloperApiError);

// Keys
export const listKeys = (tenantId) =>
  developerApi.get('/developer/keys', { params: { tenantId } }).then((r) => r.data);

export const createKey = (body) =>
  developerApi.post('/developer/keys', body).then((r) => r.data);

export const revokeKey = (keyId) =>
  developerApi.delete(`/developer/keys/${keyId}`).then((r) => r.data);

// Domains
export const listDomains = () =>
  developerApi.get('/developer/domains').then((r) => r.data);

export const registerDomain = (body) =>
  developerApi.post('/developer/domains', body).then((r) => r.data);

export const verifyDomain = (id) =>
  developerApi.post(`/developer/domains/${id}/verify`).then((r) => r.data);

export const deleteDomain = (id) =>
  developerApi.delete(`/developer/domains/${id}`).then((r) => r.data);

// Webhooks
export const listWebhooks = (tenantId) =>
  developerApi.get('/developer/webhooks', { params: { tenantId } }).then((r) => r.data);

export const createWebhook = (body) =>
  developerApi.post('/developer/webhooks', body).then((r) => r.data);

export const updateWebhook = (id, body) =>
  developerApi.put(`/developer/webhooks/${id}`, body).then((r) => r.data);

export const deleteWebhook = (id) =>
  developerApi.delete(`/developer/webhooks/${id}`).then((r) => r.data);

// API Logs
export const getApiLogs = (params) =>
  developerApi.get('/developer/logs/api', { params }).then((r) => r.data);

// Webhook deliveries
export const getWebhookDeliveries = (webhookId, params) =>
  developerApi.get(`/developer/webhooks/${webhookId}/deliveries`, { params }).then((r) => r.data);

export const retryWebhookDelivery = (webhookId, deliveryId) =>
  developerApi.post(`/developer/webhooks/${webhookId}/deliveries/${deliveryId}/retry`).then((r) => r.data);

// Scoped token — short-lived st_* for the Scalar API reference playground.
// Never expose sk_live_* or sk_test_* in the browser; this is the only credential
// that reaches the frontend. `tenant` ('live' | 'test') selects which tenant the
// token is scoped to (omitted ⇒ server defaults to test); `ttl` (60-3600s) sets
// the lifetime (omitted ⇒ server defaults to 3600s). Undefined params are not
// serialized by axios, so getScopedToken() preserves the original test/1hr call.
export const getScopedToken = (tenant, ttl) =>
  developerApi.get('/developer/scoped-token', { params: { tenant, ttl } }).then((r) => r.data);

// App contexts — /developer/app-contexts (OWNER-gated control plane, ADR-0049 / #332).
//
// IMPORTANT: list contexts via THIS developer-API route, NOT the partner API's
// GET /v1/app-contexts. The dev-portal's partner-API bearer is an st_* pinned to
// the reserved `vectros-admin` context, so it is context-confined and the partner
// list returns ONLY vectros-admin — an OWNER cannot enumerate their own data
// contexts through it. This server-side, OWNER-gated route resolves the tenant
// directly and returns all of them. Likewise, context CREATION is a control-plane
// provisioning act the browser cannot self-authorize (no provisioning:c token
// ever reaches the client), so it goes through this route too. `tenant` is
// 'live' | 'test' (defaults to 'test' on the server when absent). See #324.
const unwrapPaged = (r) => (Array.isArray(r.data) ? r.data : r.data?.data ?? []);

export const listAppContexts = (tenant) =>
  developerApi.get('/developer/app-contexts', { params: { tenant } }).then(unwrapPaged);

// body: { contextId, name?, description? }. Idempotent by contextId (200 if it
// already exists, 201 if created).
export const createAppContext = (tenant, body) =>
  developerApi.post('/developer/app-contexts', body, { params: { tenant } }).then((r) => r.data);

// Peer-invites — POST /developer/peer-invite (ADR-0053 / #315). An activated
// owner issues (or refreshes) their single redeemable invite link; the response
// carries { inviteId, redeemLink, inviteToken, expiresAt, remaining }. redeemUrl
// is the absolute base the redeem link should point at (the portal's own /redeem).
export const issuePeerInvite = (redeemUrl) =>
  developerApi.post('/developer/peer-invite', redeemUrl ? { redeemUrl } : {}).then((r) => r.data);

// Legal
export const getLegalStatus = () =>
  developerApi.get('/developer/legal/status').then((r) => r.data);

export const acceptTos = () =>
  developerApi.post('/developer/legal/accept-tos').then((r) => r.data);

export const signBaa = () =>
  developerApi.post('/developer/legal/sign-baa').then((r) => r.data);

export const getBaaRecord = () =>
  developerApi.get('/developer/legal/baa-record').then((r) => r.data);

// Data residency & region (ADR-0046 / #260). Per-tenant global-processing waiver.
// `tenant` is 'live' | 'test'. signGlobalProcessingWaiver body: { defaultGlobal, allowOverride }.
export const signGlobalProcessingWaiver = (tenant, body) =>
  developerApi
    .post('/developer/legal/sign-global-processing-waiver', body, { params: { tenant } })
    .then((r) => r.data);

export const disableGlobalProcessing = (tenant) =>
  developerApi
    .post('/developer/legal/disable-global-processing', null, { params: { tenant } })
    .then((r) => r.data);

export const getGlobalWaiverRecord = (tenant) =>
  developerApi
    .get('/developer/legal/global-waiver-record', { params: { tenant } })
    .then((r) => r.data);

// Billing
export const getBilling = () =>
  developerApi.get('/developer/billing').then((r) => r.data);

export const createCheckoutSession = (body) =>
  developerApi.post('/developer/billing/checkout', body).then((r) => r.data);

export const getCheckoutStatus = (sessionId) =>
  developerApi.get('/developer/billing/checkout-status', { params: { sessionId } }).then((r) => r.data);

export const updateOverage = (body) =>
  developerApi.post('/developer/billing/overage', body).then((r) => r.data);

// Inference balance — separate pre-paid ledger from monthly plan credits.
// See backend DeveloperBillingHandler /inference, /inference/fund, /inference/auto-recharge.
export const getInferenceBalance = () =>
  developerApi.get('/developer/billing/inference').then((r) => r.data);

export const fundInferenceBalance = (amountCents) =>
  developerApi.post('/developer/billing/inference/fund', { amountCents }).then((r) => r.data);

export const updateInferenceAutoRecharge = (body) =>
  developerApi.put('/developer/billing/inference/auto-recharge', body).then((r) => r.data);

export default developerApi;
